import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Search, Upload, Truck } from 'lucide-react';
import { supplierAPI, parseFileData } from '../utils/api';
import SupplierModal from '../components/SupplierModal';
import SupplierPaymentsModal from '../components/SupplierPaymentsModal';
import '../styles/Suppliers.css';

function Suppliers() {
    const [showPaymentsModal, setShowPaymentsModal] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [alert, setAlert] = useState(null);
  const fileInputRef = useRef(null);


  useEffect(() => {
    fetchSuppliers();
    // Add demo suppliers if none exist
    (async () => {
      try {
        const response = await supplierAPI.getAll();
        if (response.data.length === 0) {
          const demoSuppliers = [
            {
              supplierName: 'ABC Electronics',
              companyName: 'ABC Electronics Pvt Ltd',
              phoneNumber: '9876501234',
              emailId: 'contact@abcelectronics.com',
              street: '12 Tech Park',
              area: 'Sector 21',
              city: 'Noida',
              state: 'Uttar Pradesh',
              pincode: '201301',
              gstinNumber: '09ABCDE1234F1Z5',
              stateCode: '09',
              gstRegistered: true,
              totalCredit: 25000,
              totalPaid: 15000,
            },
            {
              supplierName: 'Sharma Distributors',
              companyName: 'Sharma Distributors',
              phoneNumber: '9123456780',
              emailId: 'info@sharmadistributors.com',
              street: '45 Market Road',
              area: 'Old City',
              city: 'Jaipur',
              state: 'Rajasthan',
              pincode: '302001',
              gstinNumber: '08AABCS1234E1Z2',
              stateCode: '08',
              gstRegistered: true,
              totalCredit: 12000,
              totalPaid: 12000,
            },
            {
              supplierName: 'Global Tech Supplies',
              companyName: 'Global Tech Supplies',
              phoneNumber: '9988776655',
              emailId: 'sales@globaltech.com',
              street: '88 Industrial Area',
              area: 'MIDC',
              city: 'Pune',
              state: 'Maharashtra',
              pincode: '411019',
              gstinNumber: '27AABCG5678H1Z3',
              stateCode: '27',
              gstRegistered: true,
              totalCredit: 5000,
              totalPaid: 20000,
            },
            {
              supplierName: 'Eastern Traders',
              companyName: 'Eastern Traders',
              phoneNumber: '9001122334',
              emailId: 'support@easterntraders.com',
              street: '7A Main Street',
              area: 'Salt Lake',
              city: 'Kolkata',
              state: 'West Bengal',
              pincode: '700091',
              gstinNumber: '19AABCE2345J1Z6',
              stateCode: '19',
              gstRegistered: false,
              totalCredit: 0,
              totalPaid: 5000,
            },
          ];
          await supplierAPI.bulkCreate(demoSuppliers);
          fetchSuppliers();
        }
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      handleSearch();
    } else {
      setFilteredSuppliers(suppliers);
    }
  }, [searchQuery, suppliers]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await supplierAPI.getAll();
      setSuppliers(response.data);
      setFilteredSuppliers(response.data);
    } catch (error) {
      showAlert('Error fetching suppliers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setFilteredSuppliers(suppliers);
      return;
    }

    try {
      const response = await supplierAPI.search(searchQuery);
      setFilteredSuppliers(response.data);
    } catch (error) {
      showAlert('Error searching suppliers', 'error');
    }
  };

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setShowModal(true);
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setShowModal(true);
  };

  const handlePayments = (supplier) => {
    setSelectedSupplier(supplier);
    setShowPaymentsModal(true);
  };

  const handleDeleteSupplier = async (id) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await supplierAPI.delete(id);
        showAlert('Supplier deleted successfully', 'success');
        fetchSuppliers();
      } catch (error) {
        showAlert('Error deleting supplier', 'error');
      }
    }
  };

  const handleSaveSupplier = async (formData) => {
    try {
      if (editingSupplier) {
        await supplierAPI.update(editingSupplier.id, formData);
        showAlert('Supplier updated successfully', 'success');
      } else {
        await supplierAPI.create(formData);
        showAlert('Supplier created successfully', 'success');
      }
      setShowModal(false);
      fetchSuppliers();
    } catch (error) {
      showAlert('Error saving supplier', 'error');
    }
  };

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const suppliers = await parseFileData(file);
      await supplierAPI.bulkCreate(suppliers);
      showAlert(`Successfully imported ${suppliers.length} suppliers`, 'success');
      fetchSuppliers();
    } catch (error) {
      showAlert('Error importing suppliers: ' + error.message, 'error');
    } finally {
      setLoading(false);
      event.target.value = ''; // Reset file input
    };
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Supplier Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={() => fileInputRef.current.click()}>
            <Upload size={18} />
            Import CSV/Excel
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button className="btn btn-primary" onClick={handleAddSupplier}>
            <Plus size={18} />
            Add Supplier
          </button>
        </div>
      </div>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.message}</div>}

      <div className="card">
        <div className="search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, phone, GSTIN, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="empty-state">
            <Truck size={48} />
            <p>No suppliers found</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>GSTIN</th>
                  <th>City</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td>{supplier.supplierName}</td>
                    <td>{supplier.companyName || '-'}</td>
                    <td>{supplier.phoneNumber}</td>
                    <td>{supplier.emailId || '-'}</td>
                    <td>{supplier.gstinNumber || '-'}</td>
                    <td>{supplier.city || '-'}</td>
                    <td className="actions">
                      <button 
                        className="btn-icon edit" 
                        onClick={() => handleEditSupplier(supplier)}
                        title="Edit supplier"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn-icon delete" 
                        onClick={() => handleDeleteSupplier(supplier.id)}
                        title="Delete supplier"
                      >
                        <Trash2 size={16} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handlePayments(supplier)}
                        title="Payments / Credit"
                      >
                        ₹
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <SupplierModal
          supplier={editingSupplier}
          onSave={handleSaveSupplier}
          onClose={() => setShowModal(false)}
        />
      )}
      {showPaymentsModal && selectedSupplier && (
        <SupplierPaymentsModal
          supplier={selectedSupplier}
          onClose={() => setShowPaymentsModal(false)}
        />
      )}
    </div>
  );
}

export default Suppliers;
